import { Trans } from '@lingui/macro';
import WifiIcon from 'assets/icons/WifiIcon.svg';
import ProgressCircle from 'components/ProgressCircle/ProgressCircle';
import useIsMobileLayout from 'hooks/useIsMobileLayout';
import { useScreenSizeRatio } from 'hooks/useScreenSizeRatio';
import useWindowSize from 'hooks/useWindowSize';
import { Howler } from 'howler';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { Transmission as TransmissionSound } from 'types/transmission';

import ScreenButton from '../common/ScreenButton';

type TransmissionProps = {
  transmission: TransmissionSound;
  title: string;
  author: string;
  onReplayButtonEnter?: () => void;
  onReplayButtonLeave?: () => void;
};

const Wrapper = styled.div<{ sizeRatio: number }>`
  align-self: center;
  display: flex;
  align-items: center;
  margin: ${({ sizeRatio }) => `${20 * sizeRatio}px`};
  height: 50%;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    height: 30%;
    margin: 5px;
  `};
`;
const TransmissionInfos = styled.div<{ sizeRatio: number }>`
  display: flex;
  flex-direction: column;
  margin-left: ${({ sizeRatio }) => `${30 * sizeRatio}px`};
  ${({ theme }) => theme.mediaWidth.upToMedium`
    margin-left: 15px;
  `};
`;
const State = styled.div<{ sizeRatio: number }>`
  font-size: ${({ sizeRatio }) => `${13 * sizeRatio}px`};
  margin-bottom: ${({ sizeRatio }) => `${10 * sizeRatio}px`};
  ${({ theme }) => theme.mediaWidth.upToMedium`
    font-size: 10px;
    margin-bottom: 5px;
  `};
`;
const Title = styled.div<{ sizeRatio: number }>`
  font-family: 'Presicav';
  font-size: ${({ sizeRatio }) => `${30 * sizeRatio}px`};
  font-weight: bold;
  letter-spacing: 1px;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    font-size: 18.5px;
  `};
`;
const Author = styled.div<{ sizeRatio: number }>`
  font-family: 'Presicav';
  align-self: flex-end;
  margin-top: 5px;
  font-size: ${({ sizeRatio }) => `${12 * sizeRatio}px`};
  ${({ theme }) => theme.mediaWidth.upToMedium`
    font-size: 10px;
    align-self: flex-start;
  `};
`;

const CanvasWrapper = styled.div`
  width: 100%;
  height: 50%;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    height: 20%;
  `};
`;

const Transmission: React.FC<TransmissionProps> = ({
  transmission,
  title,
  author,
  onReplayButtonEnter,
  onReplayButtonLeave,
}) => {
  const sizeRatio = useScreenSizeRatio();
  const [readyToDraw, setReadyToDraw] = useState(false);
  const [analyser, setAnalyser] = useState<AnalyserNode | undefined>(undefined);
  const [dataArray, setDataArray] = useState<Uint8Array | undefined>(undefined);
  const [bufferLength, setBufferLength] = useState<number | undefined>(undefined);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const canvasCtx = useMemo(() => {
    if (canvas && canvas.current) {
      return canvas.current.getContext('2d');
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, canvas.current]);

  const draw = function () {
    if (!canvas?.current || !canvasCtx || !analyser || !bufferLength || !dataArray) return;

    const width = canvas.current.width;
    const height = canvas.current.height;

    analyser.getByteTimeDomainData(dataArray);
    requestAnimationFrame(draw);

    let x = 0;
    const sliceWidth = (width * 1.0) / dataArray.length;

    canvasCtx.lineWidth = 2;
    canvasCtx.strokeStyle = '#00ffc9';
    canvasCtx.clearRect(0, 0, width, height);

    canvasCtx.beginPath();
    canvasCtx.moveTo(0, height / 2);
    for (const item of dataArray) {
      const y = (item / 255.0) * height;
      canvasCtx.lineTo(x, y);
      x += sliceWidth;
    }
    canvasCtx.lineTo(x, height / 2);
    canvasCtx.stroke();
  };

  useEffect(() => {
    const newAnalyser = Howler.ctx.createAnalyser();
    // Connect the masterGain -> analyser (disconnecting masterGain -> destination)
    Howler.masterGain.connect(newAnalyser);

    // Connect the analyser -> destination
    newAnalyser.connect(Howler.ctx.destination);
    newAnalyser.fftSize = 1024;
    setBufferLength(newAnalyser.frequencyBinCount);
    setDataArray(new Uint8Array(newAnalyser.fftSize));

    setAnalyser(newAnalyser);

    setReadyToDraw(true);
  }, []);

  useEffect(() => {
    if (readyToDraw && canvasCtx) {
      draw();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyToDraw, canvasCtx]);

  const { percentage, play } = transmission;
  const isMobileLayout = useIsMobileLayout();
  const { width: windowWidth } = useWindowSize();

  return (
    <>
      <Wrapper sizeRatio={sizeRatio}>
        <ProgressCircle
          sizeRatio={isMobileLayout ? 0.75 : sizeRatio}
          percentage={percentage}
          onReplay={() => play()}
          onMouseEnter={onReplayButtonEnter}
          onMouseLeave={onReplayButtonLeave}
        />
        <TransmissionInfos sizeRatio={sizeRatio}>
          <State sizeRatio={sizeRatio}>
            {percentage > 0 ? <Trans>Currently Playing...</Trans> : <span>&nbsp;</span>}
          </State>
          <Title sizeRatio={sizeRatio}>{title}</Title>
          <Author sizeRatio={sizeRatio}>{author}</Author>
        </TransmissionInfos>
      </Wrapper>
      <CanvasWrapper>
        <canvas
          ref={canvas}
          id="canvas"
          width={`${isMobileLayout ? (windowWidth ?? 500) - 40 : 556 * sizeRatio}px`}
          height={`${100 * sizeRatio}px`}
        ></canvas>
      </CanvasWrapper>

      {isMobileLayout && <ScreenButton label="Transmission" icon={WifiIcon} sizeRatio={sizeRatio} />}
    </>
  );
};

export default Transmission;
